package com.solevo.dist.viewmodels

import com.solevo.dist.application.models.Quote
import com.solevo.dist.application.models.QuoteLine
import com.solevo.dist.application.models.QuoteStatus
import com.solevo.dist.network.api.user.UserRemoteSource
import com.solevo.dist.repository.ApplicationQuoteRepository
import com.solevo.dist.utils.di.QuoteFirebaseQualifier
import com.solevo.dist.util.DateFormatter
import com.solevo.dist.util.AmountFormatter
import com.solevo.dist.util.StatusMapper
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.ExperimentalCoroutinesApi
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.cancel
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.catch
import kotlinx.coroutines.flow.filterNotNull
import kotlinx.coroutines.flow.flatMapLatest
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.launch
import kotlinx.datetime.Instant
import org.koin.mp.KoinPlatform.getKoin

data class QuoteDetailsUi(
    val quoteId: String = "",
    val referenceText: String = "",
    val orderId: String = "",
    val statusText: String = "",
    val statusColor: com.varabyte.kobweb.compose.ui.graphics.Color = com.varabyte.kobweb.compose.ui.graphics.Color.rgb(36,41,47),
    val statusColorHex: String = "#24292F",
    val createdText: String = "",
    val pickupText: String = "",
    val items: List<QuoteLineUi> = emptyList()
)

data class QuoteLineUi(
    val productId: String,
    val name: String,
    val pricePerUnitText: String,
    val warehouse: String,
    val quantityText: String,
    val totalPriceText: String
)

class QuoteDetailsViewModel(quoteId: String) {
    private val scope = CoroutineScope(SupervisorJob() + Dispatchers.Default)
    private val repository: ApplicationQuoteRepository = getKoin().get()
    private val userRemoteSource: UserRemoteSource = getKoin().get()

    private val _state = MutableStateFlow(QuoteDetailsUi())
    val state: StateFlow<QuoteDetailsUi> = _state.asStateFlow()

    fun stop() = scope.cancel()

    @OptIn(ExperimentalCoroutinesApi::class)
    fun start(quoteId: String) {
        scope.launch {
            userRemoteSource.currentUser()
                .filterNotNull()
                .flatMapLatest { user ->
                    if (user.customerId.isNotEmpty()) {
                        repository.getQuoteDetailsLive(quoteId)
                    } else {
                        kotlinx.coroutines.flow.flow { emit(null) }
                    }
                }
                .map { buildUi(it) }
                .catch { _state.value = QuoteDetailsUi(quoteId = quoteId) }
                .collect { _state.value = it }
        }
    }

    private fun buildUi(quote: Quote?): QuoteDetailsUi {
        if (quote == null) return QuoteDetailsUi()

        val statusInfo = StatusMapper.mapQuoteStatus(quote.status)
        // Note: QuoteDetails uses different colors than QuotesPage, keeping original colors for now
        val (color, hex) = when (quote.status) {
            QuoteStatus.Awaiting -> com.varabyte.kobweb.compose.ui.graphics.Color.rgb(99, 102, 241) to "#6366F1"
            QuoteStatus.Received -> com.varabyte.kobweb.compose.ui.graphics.Color.rgb(1, 163, 108) to "#01A36C"
            QuoteStatus.Error -> com.varabyte.kobweb.compose.ui.graphics.Color.rgb(221, 54, 54) to "#DD3636"
            QuoteStatus.Other -> com.varabyte.kobweb.compose.ui.graphics.Color.rgb(136, 136, 136) to "#888888"
        }
        return QuoteDetailsUi(
            quoteId = quote.quoteId,
            referenceText = quote.orderReference ?: "—",
            orderId = quote.orderReference ?: "—",
            statusText = statusInfo.label,
            statusColor = color,
            statusColorHex = hex,
            createdText = DateFormatter.formatAsDayMonthYear(quote.createdAt),
            pickupText = DateFormatter.formatAsDayMonthYear(quote.pickupDateTo),
            items = quote.quoteLines.orEmpty().map { it.toUi() }
        )
    }

    private fun QuoteLine.toUi(): QuoteLineUi {
        return QuoteLineUi(
            productId = this.productId,
            name = this.productId.ifBlank { "—" },
            pricePerUnitText = this.pricePerUnitText(),
            warehouse = this.warehouseId,
            quantityText = this.quantityText(),
            totalPriceText = AmountFormatter.formatAmountWithCurrency(this.totalPrice)
        )
    }

    private fun QuoteLine.quantityText(): String {
        return AmountFormatter.formatUnits(this.quantity, this.unit.takeIf { it.isNotBlank() })
    }

    private fun QuoteLine.pricePerUnitText(): String {
        return AmountFormatter.formatPricePerUnit(this.pricePerUnit, this.unit.takeIf { it.isNotBlank() })
    }
}


